import "./src/styles/global.scss"
import "./src/styles/font-faces.scss"

import { RouteUpdateArgs } from "gatsby"

import { SEGMENT_LOADED_EVENT_ID } from "./src/shared-components/seo/use-segment-is-loaded"
import { initWebVitals } from "./src/utils/analytics/web-vitals"
import { QUERY_PARAM_CHANGE_EVENT_ID } from "./src/utils/contexts/page-context"
import {
  getLocalStorageUserInfo,
  setLocalStorageUserInfo,
} from "./src/utils/local-storage-user-info"
import {
  ALTERNATIVE_LOCALES,
  AlternativeLocale,
  DEFAULT_LOCALE,
  getLocaleFromPathname,
} from "./src/utils/locales"
import { localizedRoutes } from "./src/utils/localized-routes"

export { wrapPageElement } from "./gatsby-shared"

export function onRouteUpdate({ location, prevLocation }: RouteUpdateArgs) {
  if (location.search !== prevLocation?.search) {
    const event = new CustomEvent(QUERY_PARAM_CHANGE_EVENT_ID, {
      detail: {
        pathname: location.pathname,
        search: location.search,
      },
    })
    document.dispatchEvent(event)
  }
}

export function onClientEntry() {
  const localStorageLocale = getLocalStorageUserInfo(
    "locale",
  ) as AlternativeLocale
  const { pathname } = window.location

  if (!localStorageLocale) {
    return
  }

  const isCurrentPageLocalized =
    pathname.startsWith(`/${localStorageLocale}/`) ||
    pathname === `/${localStorageLocale}`

  if (
    isCurrentPageLocalized ||
    !ALTERNATIVE_LOCALES.includes(localStorageLocale)
  ) {
    return
  }

  const path =
    pathname !== "/" && !pathname.endsWith("/") ? `${pathname}/` : pathname

  const expectedTargetPath =
    localizedRoutes[path]?.[
      localStorageLocale as (typeof ALTERNATIVE_LOCALES)[number]
    ]

  if (expectedTargetPath) {
    // Use native browser redirect as Gatsby navigate might not be ready yet in onClientEntry
    window.location.replace(expectedTargetPath)
  }
}

export function onInitialClientRender() {
  const locale = getLocaleFromPathname(window.location.pathname)

  // save non-English locale as user preference (session locale)
  if (locale !== DEFAULT_LOCALE) {
    setLocalStorageUserInfo({ locale })
  }

  if (window.analytics) {
    initWebVitals()
    return
  }

  const handleSegmentLoaded = () => {
    initWebVitals()
    document.removeEventListener(SEGMENT_LOADED_EVENT_ID, handleSegmentLoaded)
  }

  document.addEventListener(SEGMENT_LOADED_EVENT_ID, handleSegmentLoaded)
}
